var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { faRightToBracket } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { yupResolver } from '@hookform/resolvers/yup';
import { Accordion, AccordionDetails, AccordionSummary, Button, FormControlLabel, Link, Radio, Stack, Typography, accordionSummaryClasses, formControlLabelClasses, inputBaseClasses, outlinedInputClasses, styled, } from '@mui/material';
import _ from 'lodash';
import { useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';
import { DifferenceMode, StatusPermission } from '../../../enum';
import { useUserPermissionsAPI } from '../../app/selectors';
import { Autocomplete, FormField, NumberInput, RadioGroup } from '../../common';
import { usePlanType } from '../selectors';
import { useExceptionOptions } from '../workArrivalPattern/selectors';
var StyledAccordion = styled(Accordion)(function (_a) {
    var _b;
    var theme = _a.theme;
    return (_b = {
            display: 'flex',
            alignItems: 'flex-start',
            minWidth: 'max-content',
            gap: theme.spacing(2)
        },
        _b[theme.breakpoints.down(1380)] = {
            minWidth: 'auto',
        },
        _b);
});
var StyledAccordionSummary = styled(AccordionSummary)(function (_a) {
    var _b;
    var theme = _a.theme;
    return (_b = {
            padding: theme.spacing(2, 0, 0, 0),
            alignItems: 'flex-start'
        },
        _b[".".concat(accordionSummaryClasses.expandIconWrapper, ".Mui-expanded")] = {
            transform: 'rotateY(180deg)',
            transition: 'transform 0.4s',
        },
        _b);
});
var StyledFormControlLabel = styled(FormControlLabel)(function (_a) {
    var _b;
    var theme = _a.theme;
    return (_b = {
            marginRight: theme.spacing(0)
        },
        _b[".".concat(formControlLabelClasses.label)] = {
            fontSize: theme.typography.body2.fontSize,
        },
        _b);
});
var ExpandIcon = styled(FontAwesomeIcon)(function (_a) {
    var theme = _a.theme;
    return ({
        color: theme.palette.primary.main,
    });
});
var StyledAutocomplete = styled(Autocomplete)(function (_a) {
    var _b;
    var theme = _a.theme;
    return (_b = {
            width: '10.5rem'
        },
        _b[".".concat(outlinedInputClasses.root, ".MuiInputBase-sizeSmall")] = {
            padding: theme.spacing(1.5, 6, 1.5, 0.5),
        },
        _b);
});
var StyledAccordionDetails = styled(AccordionDetails)(function (_a) {
    var _b;
    var theme = _a.theme;
    return (_b = {
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'flex-start',
            gap: theme.spacing(2),
            padding: theme.spacing(0, 0, 1)
        },
        _b[".".concat(inputBaseClasses.root)] = {
            fontSize: theme.typography.body2.fontSize,
        },
        _b[".".concat(inputBaseClasses.input)] = {
            padding: theme.spacing(2),
        },
        _b[theme.breakpoints.down(1380)] = {
            flexWrap: 'wrap',
        },
        _b);
});
var validateForm = yup.object().shape({
    valueChange: yup.number().nullable().required(),
    startDate: yup
        .number()
        .nullable()
        .required()
        .test('startDate', 'Start date cannot be after end date', function (value) {
        var endDate = this.parent.endDate;
        return value <= endDate;
    }),
    endDate: yup
        .number()
        .nullable()
        .required()
        .test('endDate', 'End date cannot be before start date', function (value) {
        var startDate = this.parent.startDate;
        return value >= startDate;
    }),
});
export var BulkChangeAccordion = function (_a) {
    var _b = _a.name, name0 = _b[0], name1 = _b[1], title = _a.title, setShowCheckbox = _a.setShowCheckbox, _c = _a.max, max = _c === void 0 ? 9999999 : _c, _d = _a.min, min = _d === void 0 ? 1 : _d, _e = _a.decimal, decimal = _e === void 0 ? 0 : _e, selectedRows = _a.selectedRows, getValues = _a.getValues, setValue = _a.setValue;
    var t = useTranslation('wizard', { keyPrefix: 'bulkChange' }).t;
    var planType = usePlanType();
    var options = useExceptionOptions(); // col options for bulk change (week/month)
    var initialValuesRef = useRef();
    var permission = useUserPermissionsAPI();
    var _f = useForm({
        defaultValues: {
            valueChange: 0,
            startDate: 0,
            endDate: options.length - 1,
            differenceMode: DifferenceMode.Percentage,
        },
        resolver: yupResolver(validateForm),
    }), control = _f.control, handleSubmit = _f.handleSubmit, reset = _f.reset;
    useEffect(function () {
        initialValuesRef.current = _.cloneDeep(getValues(name0));
    }, [getValues, name0]);
    var bulkUpdate = function (target, input) {
        var _a;
        if (target === void 0) { target = (_a = initialValuesRef.current) !== null && _a !== void 0 ? _a : []; }
        var _b = input !== null && input !== void 0 ? input : {}, _c = _b.startDate, startDate = _c === void 0 ? 0 : _c, _d = _b.endDate, endDate = _d === void 0 ? target.length - 1 : _d, _e = _b.valueChange, valueChange = _e === void 0 ? 0 : _e, differenceMode = _b.differenceMode;
        var setTargetValue = function (val, path) {
            var prop = name1;
            var currentValue = val[prop];
            var newValue = Number(currentValue) + valueChange;
            if (differenceMode === DifferenceMode.Percentage) {
                newValue = Number(currentValue) * (1 + valueChange / 100);
            }
            newValue = Math.round(newValue * Math.pow(10, decimal)) / Math.pow(10, decimal);
            var adjustedValue = Math.max(min, Math.min(max, newValue));
            newValue = adjustedValue === Number(currentValue) ? currentValue : adjustedValue;
            setValue("".concat(path, ".").concat(prop), input ? newValue : currentValue, {
                shouldDirty: true,
            });
        };
        target.forEach(function (value, idx) {
            if (idx < startDate || idx > endDate)
                return;
            if (!Array.isArray(value)) {
                return setTargetValue(value, "".concat(name0, ".").concat(idx));
            }
            value.forEach(function (v, rowIdx) {
                if (!input || (selectedRows === null || selectedRows === void 0 ? void 0 : selectedRows.includes(rowIdx))) {
                    setTargetValue(v, "".concat(name0, ".").concat(idx, ".").concat(rowIdx));
                }
            });
        });
    };
    var handleApply = handleSubmit(function (input) {
        bulkUpdate(getValues(name0), input);
    });
    var handleReset = function () {
        bulkUpdate();
        reset();
    };
    var handleAccordionChange = function (event, expanded) {
        setShowCheckbox === null || setShowCheckbox === void 0 ? void 0 : setShowCheckbox(expanded);
    };
    return (_jsxs(Stack, __assign({ direction: "row", gap: 2, flexWrap: "nowrap", alignItems: "center", flexShrink: 1 }, { children: [_jsx(Typography, __assign({ variant: "subtitle2", mb: 3 }, { children: title })), _jsxs(StyledAccordion, __assign({ disableGutters: true, elevation: 0, onChange: function (event, expanded) { return handleAccordionChange(event, expanded); } }, { children: [_jsx(StyledAccordionSummary, { expandIcon: _jsx(ExpandIcon, { icon: faRightToBracket, size: "lg" }), disabled: permission.capacityPermission !== StatusPermission.Edit }), _jsxs(StyledAccordionDetails, { children: [_jsx(Typography, __assign({ sx: { pt: 2 }, variant: "body2" }, { children: t('changeAllValues') })), _jsx(FormField, __assign({ required: true, name: "valueChange", control: control }, { children: _jsx(NumberInput, { max: max, decimal: decimal, min: -max, sx: { maxWidth: '4rem' } }) })), _jsx(FormField, __assign({ required: true, name: "differenceMode", control: control }, { children: _jsxs(RadioGroup, __assign({ row: true, sx: { flexWrap: 'nowrap', gap: 2 } }, { children: [_jsx(StyledFormControlLabel, { value: DifferenceMode.Percentage, label: t('percentage'), control: _jsx(Radio, {}) }), _jsx(StyledFormControlLabel, { value: DifferenceMode.Absolute, label: t('absolute'), control: _jsx(Radio, {}) })] })) })), _jsx(FormField, __assign({ required: true, name: "startDate", control: control }, { children: _jsx(StyledAutocomplete, { options: options, placeholder: t('placeholder', { planType: planType }) }) })), _jsx(Typography, __assign({ sx: { pt: 2 }, variant: "body2" }, { children: t('to') })), _jsx(FormField, __assign({ required: true, name: "endDate", control: control }, { children: _jsx(StyledAutocomplete, { options: options, placeholder: t('placeholder', { planType: planType }) }) })), _jsxs(Stack, __assign({ direction: "row", gap: 3, alignItems: "center", pt: 0.5 }, { children: [_jsx(Button, __assign({ variant: "outlined", onClick: handleApply, disabled: selectedRows && !selectedRows.length, size: "small" }, { children: t('apply') })), _jsx(Link, __assign({ component: "button", type: "button", onClick: handleReset, variant: "body2" }, { children: t('reset') }))] }))] })] }))] })));
};
